import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { Tables } from '@limo/database'
import { calculateBoundingBox, type Coordinate } from '@limo/shared/pricing'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Plus, Pencil, Trash2, MapPin, ArrowLeftRight, ArrowRight } from 'lucide-react'

import { ZoneMapEditor } from '@/components/flat-rate/ZoneMapEditor'
import { ZoneDialog } from '@/components/flat-rate/ZoneDialog'
import { OverrideDialog } from '@/components/flat-rate/OverrideDialog'

type FlatRateZone = Tables<'flat_rate_zones'>
type FlatRateOverride = Tables<'flat_rate_overrides'>

// Override with joined zone data
interface FlatRateOverrideWithZones extends FlatRateOverride {
  pickup_zone: FlatRateZone
  dropoff_zone: FlatRateZone
}

export function FlatRatePricingPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Dialog states
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false)
  const [editingZone, setEditingZone] = useState<FlatRateZone | null>(null)
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false)
  const [editingOverride, setEditingOverride] = useState<FlatRateOverrideWithZones | null>(null)
  const [deleteZoneDialogOpen, setDeleteZoneDialogOpen] = useState(false)
  const [deletingZone, setDeletingZone] = useState<FlatRateZone | null>(null)
  const [deleteOverrideDialogOpen, setDeleteOverrideDialogOpen] = useState(false)
  const [deletingOverride, setDeletingOverride] = useState<FlatRateOverrideWithZones | null>(null)

  // Selected zone for map highlighting
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null)

  // Pending polygon from map drawing
  const [pendingPolygon, setPendingPolygon] = useState<Coordinate[] | null>(null)

  // Fetch zones
  const { data: zones, isLoading: zonesLoading } = useQuery({
    queryKey: ['flat-rate-zones', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      const { data, error } = await supabase
        .from('flat_rate_zones')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('name', { ascending: true })

      if (error) throw error
      return data || []
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch overrides with zones
  const { data: overrides, isLoading: overridesLoading } = useQuery({
    queryKey: ['flat-rate-overrides', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      const { data, error } = await supabase
        .from('flat_rate_overrides')
        .select(`
          *,
          pickup_zone:flat_rate_zones!pickup_zone_id(*),
          dropoff_zone:flat_rate_zones!dropoff_zone_id(*)
        `)
        .eq('account_id', activeAccount.id)
        .order('priority', { ascending: true })

      if (error) throw error
      return (data || []) as FlatRateOverrideWithZones[]
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch vehicles for override dialog
  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('account_id', activeAccount.id)
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (error) throw error
      return data || []
    },
    enabled: !!activeAccount?.id,
  })

  // Delete zone mutation
  const deleteZoneMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('flat_rate_zones').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flat-rate-zones', activeAccount?.id] })
      queryClient.invalidateQueries({ queryKey: ['flat-rate-overrides', activeAccount?.id] })
      toast({
        title: 'Zone deleted',
        description: 'The zone and all associated overrides have been removed.',
      })
      setDeleteZoneDialogOpen(false)
      setDeletingZone(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Delete override mutation
  const deleteOverrideMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('flat_rate_overrides').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flat-rate-overrides', activeAccount?.id] })
      toast({
        title: 'Flat rate deleted',
        description: 'The flat rate override has been removed.',
      })
      setDeleteOverrideDialogOpen(false)
      setDeletingOverride(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Handle zone creation from map drawing
  const handleZoneCreate = (polygon: Coordinate[]) => {
    setPendingPolygon(polygon)
    setEditingZone(null)
    setZoneDialogOpen(true)
  }

  // Handle zone editing from map
  const handleZoneUpdate = async (zoneId: string, polygon: Coordinate[]) => {
    const zone = zones?.find((z) => z.id === zoneId)
    if (!zone) return

    const boundingBox = calculateBoundingBox(polygon)

    const { error } = await supabase
      .from('flat_rate_zones')
      .update({
        polygon,
        bounding_box: boundingBox,
      })
      .eq('id', zoneId)

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to update zone polygon.',
        variant: 'destructive',
      })
      return
    }

    queryClient.invalidateQueries({ queryKey: ['flat-rate-zones', activeAccount?.id] })
    toast({
      title: 'Zone updated',
      description: 'Zone boundaries have been updated.',
    })
  }

  const handleEditZone = (zone: FlatRateZone) => {
    setPendingPolygon(null)
    setEditingZone(zone)
    setZoneDialogOpen(true)
  }

  const handleDeleteZone = (zone: FlatRateZone) => {
    setDeletingZone(zone)
    setDeleteZoneDialogOpen(true)
  }

  const handleAddOverride = () => {
    setEditingOverride(null)
    setOverrideDialogOpen(true)
  }

  const handleEditOverride = (override: FlatRateOverrideWithZones) => {
    setEditingOverride(override)
    setOverrideDialogOpen(true)
  }

  const handleDeleteOverride = (override: FlatRateOverrideWithZones) => {
    setDeletingOverride(override)
    setDeleteOverrideDialogOpen(true)
  }

  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price)
  }

  // Get vehicle count text
  const getVehicleCountText = (vehicleIds: string[] | null) => {
    if (!vehicleIds) return 'All Vehicles'
    if (vehicleIds.length === 1) return '1 Vehicle'
    return `${vehicleIds.length} Vehicles`
  }

  const isLoading = zonesLoading || overridesLoading

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-[300px] w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Zones Section */}
      <Card>
        <CardHeader>
          <CardTitle>Geographic Zones</CardTitle>
          <CardDescription>
            Draw zones on the map to define pickup and dropoff areas for flat rate pricing.
            Click a zone in the list to highlight it on the map.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Map Editor */}
          <ZoneMapEditor
            zones={zones || []}
            selectedZoneId={selectedZoneId}
            onZoneCreate={handleZoneCreate}
            onZoneSelect={setSelectedZoneId}
            onZoneUpdate={handleZoneUpdate}
          />

          {/* Zones Table */}
          {zones && zones.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {zones.map((zone) => (
                  <TableRow
                    key={zone.id}
                    className={selectedZoneId === zone.id ? 'bg-secondary/50' : ''}
                    onClick={() => setSelectedZoneId(zone.id === selectedZoneId ? null : zone.id)}
                  >
                    <TableCell>
                      <div
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: zone.color }}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{zone.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {zone.description || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={zone.is_active ? 'default' : 'secondary'}>
                        {zone.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditZone(zone)
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteZone(zone)
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6 text-muted-foreground border rounded-lg">
              <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No zones created yet.</p>
              <p className="text-sm">Use the drawing tools on the map above to create your first zone.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Flat Rate Overrides Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Flat Rate Overrides</CardTitle>
            <CardDescription>
              Define fixed prices for trips between specific zone pairs.
              When a booking matches, the flat rate replaces distance-based pricing.
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={handleAddOverride}
            disabled={!zones || zones.length < 1}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Flat Rate
          </Button>
        </CardHeader>
        <CardContent>
          {overrides && overrides.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Flat Rate</TableHead>
                  <TableHead>Vehicles</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overrides.map((override) => (
                  <TableRow key={override.id}>
                    <TableCell className="font-medium">{override.name}</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-1">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: override.pickup_zone.color }}
                        />
                        <span>{override.pickup_zone.name}</span>
                        {override.direction === 'bidirectional' ? (
                          <ArrowLeftRight className="h-3 w-3 mx-1" />
                        ) : (
                          <ArrowRight className="h-3 w-3 mx-1" />
                        )}
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: override.dropoff_zone.color }}
                        />
                        <span>{override.dropoff_zone.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {override.direction === 'bidirectional' ? 'Both ways' : 'One way'}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatPrice(override.flat_rate)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {getVehicleCountText(override.vehicle_ids)}
                    </TableCell>
                    <TableCell>{override.priority}</TableCell>
                    <TableCell>
                      <Badge variant={override.is_active ? 'default' : 'secondary'}>
                        {override.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditOverride(override)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteOverride(override)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-6 text-muted-foreground border rounded-lg">
              <ArrowLeftRight className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No flat rate overrides configured.</p>
              <p className="text-sm">
                {zones && zones.length > 0
                  ? 'Create flat rate rules to offer fixed pricing between zones.'
                  : 'Create at least one zone first, then add flat rate overrides.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Zone Dialog */}
      {activeAccount && (
        <ZoneDialog
          open={zoneDialogOpen}
          onOpenChange={(open) => {
            setZoneDialogOpen(open)
            if (!open) {
              setPendingPolygon(null)
              setEditingZone(null)
            }
          }}
          zone={editingZone}
          pendingPolygon={pendingPolygon}
          accountId={activeAccount.id}
        />
      )}

      {/* Override Dialog */}
      {activeAccount && (
        <OverrideDialog
          open={overrideDialogOpen}
          onOpenChange={(open) => {
            setOverrideDialogOpen(open)
            if (!open) setEditingOverride(null)
          }}
          override={editingOverride}
          zones={zones || []}
          vehicles={vehicles || []}
          accountId={activeAccount.id}
        />
      )}

      {/* Delete Zone Confirmation Dialog */}
      <AlertDialog open={deleteZoneDialogOpen} onOpenChange={setDeleteZoneDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Zone</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingZone?.name}"? This will also delete
              all flat rate overrides that use this zone. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingZone && deleteZoneMutation.mutate(deletingZone.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteZoneMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Override Confirmation Dialog */}
      <AlertDialog open={deleteOverrideDialogOpen} onOpenChange={setDeleteOverrideDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Flat Rate</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingOverride?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingOverride && deleteOverrideMutation.mutate(deletingOverride.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteOverrideMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
